import { useEffect, useState } from 'react';
import type { AdminSpotResponse } from '../../types/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  open: boolean;
  spot: AdminSpotResponse | null;
  onClose: () => void;
  onSave: (name: string, latitude: number, longitude: number) => Promise<void>;
}

export function SpotFormDialog({ open, spot, onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(spot?.name ?? '');
      setLatitude(spot != null ? String(spot.latitude) : '');
      setLongitude(spot != null ? String(spot.longitude) : '');
      setError(null);
    }
  }, [open, spot]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (isNaN(lat) || lat < -90 || lat > 90) { setError('Latitude must be between -90 and 90.'); return; }
    if (isNaN(lon) || lon < -180 || lon > 180) { setError('Longitude must be between -180 and 180.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(name.trim(), lat, lon);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save spot.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {spot ? 'Edit Spot' : 'New Spot'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className={inputClass} placeholder="e.g. North Dock" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Latitude</label>
              <input value={latitude} onChange={e => setLatitude(e.target.value)} className={inputClass} placeholder="-90 to 90" type="number" step="any" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Longitude</label>
              <input value={longitude} onChange={e => setLongitude(e.target.value)} className={inputClass} placeholder="-180 to 180" type="number" step="any" />
            </div>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
